/**
 * A CLIENT'S OWN CSV to the bar shape `POST /api/simulation/*` accepts.
 *
 * <h2>This parser must agree with the backend EXACTLY — neither stricter nor looser</h2>
 *
 * The backend's `CustomSeriesPayload` validates the same series again, which is right: a limit that
 * lives only in the client is not a limit. But the two must agree on WHAT is acceptable, and both
 * directions of disagreement are bugs:
 *
 * - **Looser here** and a file passes the browser, uploads, and is refused by the server. The user
 *   has waited for a ten-megabyte round trip to be told something we knew before sending.
 * - **Stricter here** and a file the engine would happily run is refused before it is ever offered.
 *   That is the worse failure, because there is no error to read on the server and nothing to
 *   compare against — the feature simply appears not to work for that file.
 *
 * So the rules below are deliberately a mirror of two Java definitions, and
 * `parseOhlcCsv.contract.test.js` reads both off disk and pins them against these constants.
 *
 * <h2>What is accepted</h2>
 *
 * A header row naming at least date, open, high, low and close, in any order and any case, plus
 * optional volume. Extra columns (`Adj Close`, anything else) are ignored rather than refused —
 * every export from Yahoo, a broker or a spreadsheet carries some. Rows are comma-separated;
 * CRLF, a UTF-8 BOM and blank trailing lines are all tolerated because every real file has them.
 */

/**
 * THE MINIMUM, mirroring `CustomSeriesPayload.MIN_DAILY_BARS`.
 *
 * <p>Not a round number. The macro regime gate reads a 200-period average and an UNKNOWN regime
 * VETOES, so a shorter series does not produce a cautious run — it produces one in which every
 * entry is refused for want of history, and the report reads as a strategy that found no setups.
 * ATR(14) and the 20-bar channel have the same shape of requirement at smaller scale. The margin
 * above 200 is so there are bars left in which the engine can actually trade once the averages
 * exist.
 */
export const MIN_ROWS = 250;

/** Mirrors `CustomSeriesPayload.MAX_TOTAL_BARS`: a ceiling, so a mistake fails with a sentence. */
export const MAX_ROWS = 400000;

/** How many row-level problems to report before summarising the rest. */
const MAX_REPORTED_ERRORS = 10;

/**
 * A number this platform can store and read back.
 *
 * <h3>Why a regex and not `Number()`</h3>
 *
 * `Number()` and Java's `Double.parseDouble` disagree in both directions, and each disagreement is
 * a file that behaves differently in the browser than on the server:
 *
 * | value      | `Number()`  | `parseDouble` |
 * |------------|-------------|---------------|
 * | `0x10`     | 16          | throws        |
 * | `1d`       | NaN         | 1.0           |
 * | `Infinity` | Infinity    | Infinity      |
 * | `" 1 "`    | 1           | 1             |
 *
 * `EodRecordFormat.isWritablePrice` already rejects the whitespace case explicitly, for exactly
 * this reason — `parseDouble` trims, so a value with a space in it would be written and then read
 * back as a different number of columns. This regex is narrower than both engines and so cannot
 * disagree with either: plain decimal digits, an optional sign, an optional fraction, and optional
 * scientific notation.
 *
 * <p>Scientific notation is DELIBERATELY accepted. `9.9999997E-5` is in real stored price data and
 * reads back correctly, so refusing it would reject a file the platform's own store contains.
 */
const NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/** An ISO calendar date, which is the only form a stored record uses. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const HEADER_ALIASES = {
  date: ['date', 'time', 'timestamp', 'datetime', 'day'],
  open: ['open', 'o'],
  high: ['high', 'h'],
  low: ['low', 'l'],
  close: ['close', 'c', 'last'],
  volume: ['volume', 'vol', 'v'],
};

/**
 * Checks a price the way the backend does, and says which test it failed.
 *
 * <p>Mirrors `isWritablePrice` AND `isValidPrice`: survives the round trip, and is actually a
 * price. Zero is excluded rather than repaired — the platform's own store contains a real session
 * with a zero close, and a long exiting on that bar books a 100% loss that reads as a catastrophic
 * trade rather than as a bad tick.
 *
 * @returns {{ value: number } | { error: string }}
 */
export const readPrice = (raw, field) => {
  if (raw === undefined || raw === null || raw === '') {
    return { error: `no ${field}` };
  }
  // Checked BEFORE trimming, and before the numeric test, because this is the whole reason the
  // Java guard exists: a value containing a comma adds a seventh column and one containing a
  // newline splits the record, yet parseDouble would accept both after trimming them away.
  if (/[\s,]/.test(raw)) {
    return { error: `${field} "${raw}" contains a space or a comma` };
  }
  if (!NUMERIC.test(raw)) {
    return { error: `${field} "${raw}" is not a number` };
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return { error: `${field} "${raw}" is not finite` };
  }
  if (value <= 0) {
    return { error: `${field} is ${value}; prices must be greater than zero` };
  }
  return { value };
};

/**
 * Volume, which has different rules from a price.
 *
 * <p>Absent becomes 0, the stored convention — `node.path("volume").asLong(0L)` on the ingestion
 * side does the same. Worth knowing rather than assuming: `DataParser.fixClosingPrice` rewrites the
 * close to the high whenever volume is 0, so a file with no volume column is not identical to one
 * with real volumes. That rule belongs to the chart path rather than this one, and it is the reason
 * volume is read at all instead of being ignored.
 */
const readVolume = (raw) => {
  if (raw === undefined || raw === null || raw === '') return { value: 0 };
  if (/[\s,]/.test(raw)) return { error: `volume "${raw}" contains a space or a comma` };
  if (!NUMERIC.test(raw)) return { error: `volume "${raw}" is not a number` };
  const value = Number(raw);
  if (!Number.isFinite(value)) return { error: `volume "${raw}" is not finite` };
  if (value < 0) return { error: `volume is negative` };
  return { value: Math.round(value) };
};

/** Splits a line on commas, trimming each cell. Quoted fields are not supported — see below. */
const cells = (line) => line.split(',').map((c) => c.trim());

/**
 * Maps the header row to column indices.
 *
 * <p>Required columns are refused by NAME when missing, rather than falling back to position. A
 * positional fallback is the single most dangerous convenience available here: a file whose columns
 * are `Date,Close,Open,High,Low` would parse, run, and produce a complete and entirely meaningless
 * ledger with nothing to indicate it.
 */
export const mapHeader = (headerLine) => {
  const found = cells(headerLine).map((c) => c.toLowerCase().replace(/^"|"$/g, ''));
  const index = {};
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    index[field] = found.findIndex((c) => aliases.includes(c));
  }
  const missing = ['date', 'open', 'high', 'low', 'close'].filter((f) => index[f] < 0);
  if (missing.length) {
    return {
      error:
        `the header row is missing ${missing.join(', ')}. Found: ${found.join(', ') || '(nothing)'}. `
        + 'A header naming date, open, high, low and close is required — columns are matched by '
        + 'name, never by position.',
    };
  }
  return { index };
};

/**
 * Parses a CSV into bars, or into the reasons it cannot be used.
 *
 * @returns {{ ok: true, bars: Array, meta: object } | { ok: false, errors: string[], meta: object }}
 */
export const parseOhlcCsv = (text) => {
  const meta = { rows: 0, firstDate: null, lastDate: null, hasVolume: false, skippedBlank: 0 };

  if (typeof text !== 'string' || !text.trim()) {
    return { ok: false, errors: ['the file is empty'], meta };
  }

  // The byte-order mark is written as the \uFEFF ESCAPE, never as the character itself: a
  // literal BOM in source is invisible to a reviewer, and eslint rejects it as irregular
  // whitespace for exactly that reason. Excel writes one, and left in place it makes the first
  // header cell "\uFEFFdate" -- so the date column is reported missing on a file that has one.
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);

  let headerAt = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim()) {
      headerAt = i;
      break;
    }
  }
  if (headerAt < 0) {
    return { ok: false, errors: ['the file is empty'], meta };
  }

  const mapped = mapHeader(lines[headerAt]);
  if (mapped.error) {
    return { ok: false, errors: [mapped.error], meta };
  }
  const index = mapped.index;
  meta.hasVolume = index.volume >= 0;

  const bars = [];
  const errors = [];
  let suppressed = 0;
  let previousDate = null;

  const fail = (message) => {
    if (errors.length < MAX_REPORTED_ERRORS) errors.push(message);
    else suppressed += 1;
  };

  for (let i = headerAt + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) {
      meta.skippedBlank += 1;
      continue;
    }
    // The line number a human sees in their editor, which is 1-based and counts the header.
    const where = `line ${i + 1}`;
    const row = cells(line);

    const rawDate = (row[index.date] || '').replace(/^"|"$/g, '');
    if (!rawDate) {
      fail(`${where}: no date`);
      continue;
    }
    if (!ISO_DATE.test(rawDate)) {
      fail(`${where}: date "${rawDate}" is not yyyy-MM-dd`);
      continue;
    }
    // Parsed as UTC, never with the local-time constructor. `new Date('2020-01-01')` is already
    // UTC, but `new Date(2020, 0, 1)` is not, and this repository has been bitten twice by a date
    // crossing a day boundary for readers west of UTC. The string compare below needs no Date at
    // all, so the only use of one is the calendar-validity check.
    const asDate = new Date(`${rawDate}T00:00:00Z`);
    if (Number.isNaN(asDate.getTime()) || asDate.toISOString().slice(0, 10) !== rawDate) {
      fail(`${where}: "${rawDate}" is not a real date`);
      continue;
    }

    // ASCENDING AND UNIQUE, compared as STRINGS. ISO dates sort lexicographically, so this needs
    // no parsing and cannot disagree with the backend's LocalDate comparison.
    if (previousDate !== null && rawDate <= previousDate) {
      fail(
        rawDate === previousDate
          ? `${where}: ${rawDate} appears twice`
          : `${where}: ${rawDate} comes after ${previousDate} in the file but is an earlier date`,
      );
      continue;
    }

    const open = readPrice(row[index.open], 'open');
    const high = readPrice(row[index.high], 'high');
    const low = readPrice(row[index.low], 'low');
    const close = readPrice(row[index.close], 'close');
    const bad = [open, high, low, close].find((r) => r.error);
    if (bad) {
      fail(`${where}: ${bad.error}`);
      continue;
    }
    if (high.value < low.value) {
      fail(`${where}: the high (${high.value}) is below the low (${low.value})`);
      continue;
    }
    // DELIBERATELY NOT CHECKED: whether open and close sit inside [low, high]. About 10,200 rows
    // across 344 files in this platform's own store violate that, straight from the provider, so
    // refusing it would hold an uploaded file to a stricter standard than the data every published
    // figure was measured on. The backend tolerates it for the same reason.

    const volume = index.volume >= 0 ? readVolume(row[index.volume]) : { value: 0 };
    if (volume.error) {
      fail(`${where}: ${volume.error}`);
      continue;
    }

    previousDate = rawDate;
    bars.push({
      date: rawDate,
      open: open.value,
      high: high.value,
      low: low.value,
      close: close.value,
      volume: volume.value,
    });

    if (bars.length > MAX_ROWS) {
      return {
        ok: false,
        errors: [`the file holds more than ${MAX_ROWS.toLocaleString()} rows`],
        meta,
      };
    }
  }

  meta.rows = bars.length;
  meta.firstDate = bars.length ? bars[0].date : null;
  meta.lastDate = bars.length ? bars[bars.length - 1].date : null;

  if (suppressed > 0) {
    errors.push(`…and ${suppressed} more problem${suppressed === 1 ? '' : 's'}.`);
  }
  // A ROW-LEVEL PROBLEM IS FATAL, not a skipped row. The stored reader skips an unreadable line
  // because a hand-edited file losing one bar of a research run beats refusing to start; a file
  // somebody has just chosen is a different situation, and silently dropping rows from it would
  // make the bar count disagree with their spreadsheet for a reason nothing on screen explains.
  if (errors.length) {
    return { ok: false, errors, meta };
  }

  if (bars.length < MIN_ROWS) {
    return {
      ok: false,
      errors: [
        `${bars.length} usable row${bars.length === 1 ? '' : 's'} — at least ${MIN_ROWS} are `
        + 'needed. The macro regime filter reads a 200-bar average and refuses to trade until it '
        + 'exists, and ATR(14) and the 20-bar breakout channel need to warm up after that. A '
        + 'shorter series does not run cautiously: every entry is vetoed for want of history, and '
        + 'the report reads as a strategy that found no setups.',
      ],
      meta,
    };
  }

  return { ok: true, bars, meta };
};

/**
 * The request body for the custom-run endpoints.
 *
 * <p>Separate from the parser so the shape of the wire is in one place and the parser stays a pure
 * function of its text. `bars` rather than `series` because this is the single-timeframe form,
 * which is read as daily — an intraday series needs a timestamp per bar and the backend refuses it
 * explicitly rather than mis-placing it.
 */
export const toCustomSeries = (bars) => ({ bars });

export default parseOhlcCsv;
